import type { RunTimeLayoutConfig } from "@umijs/max";
import { getIntl, history } from "@umijs/max";
import { PlatformShellActions, buildPlatformShellAvatarProps } from "@airange/ui";
import { App as AntdApp, ConfigProvider } from "antd";
import type { Settings as LayoutSettings } from "@ant-design/pro-components";
import "antd/dist/reset.css";
import { ApiError } from "@airange/shared/api";
import { buildDocumentTitle } from "@airange/shared/page-titles";
import { logAirangeBuildInfo } from "@airange/shared/browser-build-info";
import { LOGIN_PATH, OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";
import defaultSettings from "@/app/default-settings";
import { LocaleSwitch } from "./components/LocaleSwitch";
import { PORTAL_BASE_URL } from "./lib/env";
import { initializeEduAdminLocale } from "./lib/locale";
import { eduApi } from "./lib/edu-api";
import { clearSession, getStoredUser, setStoredSsoNoAccessSnapshot } from "./lib/session";
import { bootstrapSession, logout } from "./lib/sso";
import { toAppLocalPathname, toBrowserPath } from "./lib/public-path";
import "./styles.css";

logAirangeBuildInfo(__AIRANGE_BUILD_INFO__);

const loginPath = LOGIN_PATH;

type PageTitleRenderProps = {
  location?: {
    pathname?: string;
  };
  title?: string | false;
  pageName?: string;
};

export async function getInitialState() {
  initializeEduAdminLocale();
  const settings = defaultSettings as Partial<LayoutSettings>;
  const pathname = readCurrentPathname();
  if (pathname === OIDC_CALLBACK_PATH || pathname === loginPath) {
    return { settings, currentUser: null, eduUser: null };
  }
  let currentUser = null;
  try {
    currentUser = await bootstrapSession();
    if (!currentUser) {
      return { settings, currentUser: null, eduUser: null };
    }
    const eduUser = await eduApi.getMe();
    return { settings, currentUser, eduUser };
  } catch (error) {
    if (error instanceof ApiError && error.status === 403) {
      clearSession();
      const storedUser = currentUser ?? getStoredUser();
      if (storedUser) {
        setStoredSsoNoAccessSnapshot({ user: storedUser });
      }
      return { settings, currentUser: null, eduUser: null };
    }
    clearSession();
    return { settings, currentUser: null, eduUser: null };
  }
}

function readCurrentPathname(): string {
  if (typeof window !== "undefined") {
    return toAppLocalPathname(window.location.pathname || "/");
  }
  return "/";
}

function redirectWithReturn(pathname: string, search: string, hash: string) {
  const redirect = `${pathname}${search}${hash}` || "/";
  return `${loginPath}?redirect=${encodeURIComponent(redirect)}`;
}

export const layout: RunTimeLayoutConfig = ({ initialState }) => {
  const currentUser = initialState?.currentUser ?? null;
  const intl = getIntl();
  const centerTitle = intl.formatMessage({ id: "edu.shell.title" });
  const siteTitle = intl.formatMessage({ id: "platform.admin.title" });

  return {
    ...initialState?.settings,
    title: centerTitle,
    menu: {
      locale: true,
    },
    pageTitleRender: (props: PageTitleRenderProps, defaultPageTitle?: string) => {
      const pathname = String(props?.location?.pathname || history.location.pathname || "");
      if (pathname === OIDC_CALLBACK_PATH || pathname === loginPath) {
        return buildDocumentTitle({
          kind: pathname === loginPath ? "login" : "callback",
          siteTitle,
          pageTitle: pathname === loginPath ? intl.formatMessage({ id: "edu.shell.loginTitle" }) : "",
        });
      }
      const pageTitleCandidates = [props?.pageName, defaultPageTitle, props?.title]
        .map((item) => String(item || "").trim())
        .filter(Boolean)
        .filter((item) => item !== centerTitle && item !== siteTitle);
      return buildDocumentTitle({
        siteTitle,
        centerTitle,
        pageTitle: pageTitleCandidates[0] || centerTitle,
      });
    },
    onPageChange: () => {
      const { pathname, search, hash } = history.location;
      if (pathname === OIDC_CALLBACK_PATH || pathname === loginPath) return;
      if (!currentUser) {
        history.replace(redirectWithReturn(pathname, search, hash));
        return;
      }
      if (pathname === "/") history.replace("/dashboard");
    },
    actionsRender: () => PlatformShellActions({
      currentApp: "edu-admin",
      localeNode: <LocaleSwitch />,
      portalBaseUrl: PORTAL_BASE_URL,
    }),
    avatarProps: buildPlatformShellAvatarProps({
      currentUser,
      onLogout: async () => {
        await logout();
        window.location.assign(toBrowserPath(`${loginPath}?logged_out=1`));
      },
    }),
    childrenRender: (children) => (
      <ConfigProvider theme={{ cssVar: { key: "edu-admin" } }}>
        <AntdApp>{children}</AntdApp>
      </ConfigProvider>
    ),
  };
};
